import React, { useEffect, useState } from "react";
import { X, Star } from "lucide-react";

const DISMISS_KEY = "doctodays_smart_banner_dismissed";
const APP_STORE_URL = "https://apps.apple.com/app/doctodays/id0000000000";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.doctodays.mobile";

/**
 * Smart app banner:
 * - On iOS Safari: displayed as <meta apple-itunes-app> (native banner) — see index.html
 * - On Android Chrome: custom HTML banner that proposes opening the app (App Links)
 *   or downloading it from the Play Store.
 * - Dismissible — choice is remembered for 7 days via localStorage.
 */
export default function SmartAppBanner() {
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState(null);

  useEffect(() => {
    try {
      const dismissed = localStorage.getItem(DISMISS_KEY);
      if (dismissed && Date.now() - Number(dismissed) < 7 * 24 * 60 * 60 * 1000) return;
    } catch {}

    const ua = navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isAndroid = /Android/.test(ua);
    if (isAndroid) {
      setPlatform("android");
      setVisible(true);
    } else if (isIOS) {
      // iOS: rely on <meta name="apple-itunes-app"> in index.html (cleaner UX)
      // but also show our banner as fallback if the meta is not honored
      setPlatform("ios");
      setVisible(true);
    }
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setVisible(false);
  };

  const storeUrl = platform === "ios" ? APP_STORE_URL : PLAY_STORE_URL;

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-emerald-200 shadow-2xl px-4 py-3 flex items-center gap-3 md:hidden">
      <button
        onClick={dismiss}
        aria-label="Fermer"
        className="p-1 rounded-full hover:bg-slate-100 text-slate-400"
        data-testid="smart-banner-close"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="w-12 h-12 rounded-xl bg-emerald-600 flex items-center justify-center text-white font-bold shrink-0 relative">
        DD
        <span className="absolute -top-1 -right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center">
          <span className="text-emerald-600 text-xs font-bold">+</span>
        </span>
      </div>

      <div className="flex-1 min-w-0">
        <div className="font-semibold text-slate-900 truncate">DoctoDays</div>
        <div className="text-xs text-slate-500 truncate">
          Votre santé, en quelques clics
        </div>
        <div className="flex items-center gap-1 mt-0.5">
          {[1, 2, 3, 4, 5].map((i) => (
            <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
          ))}
          <span className="text-xs text-slate-400 ml-1">Gratuit</span>
        </div>
      </div>

      <a
        href={storeUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-4 py-2 rounded-full text-sm"
        data-testid="smart-banner-install-btn"
      >
        Installer
      </a>
    </div>
  );
}
